function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import { useDrag } from "@use-gesture/react";
import styled, { css } from "styled-components";
import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import useDebounce from "react-use/lib/useDebounce";
import map from "astrid-helpers/src/map";
import color from "../../../helpers/color";
import { transparentizeColor } from "../../../helpers/transparentize";
import Label from "../../Data/Label";
import { getRect } from "./state/rect";
import { useRipple } from "./state/ripple";
import { useScope } from "./state/scope";
import { getSize } from "./state/size";
const types = {
  striped: props => css(["background-image:repeating-linear-gradient( 45deg,transparent,transparent 6px,", " 6px,", " 8px );"], color(props), color(props)),
  bordered: props => css(["border:2px solid ", ";"], color(props)),
  default: props => css(["background-color:", ";"], transparentizeColor(props))
};
const Root = /*#__PURE__*/memo(styled.div.withConfig({
  displayName: "Root",
  componentId: "sc-lnr4fl-0"
})(["", " position:absolute;top:", "px;left:0;bottom:-2px;pointer-events:none;", ""], props => types[props.type](props), ({
  top
}) => top !== undefined ? top : -2, ({
  hide
}) => hide && css(["opacity:0;transition:opacity 0.2s ease-in-out;pointer-events:all;&:hover{opacity:1;}"])));
Root.defaultProps = {
  type: "default"
};
const StyledLabel = /*#__PURE__*/memo(styled(Label).withConfig({
  displayName: "StyledLabel",
  componentId: "sc-lnr4fl-1"
})(["position:absolute;z-index:3;top:15px;left:50%;min-width:70px;background-color:", ";color:white;font-size:14px;text-align:center;white-space:nowrap;transform:translateX(-50%);"], color));
const Handle = /*#__PURE__*/memo(styled.div.withConfig({
  displayName: "Handle",
  componentId: "sc-lnr4fl-2"
})(["position:absolute;z-index:1;bottom:25px;width:12px;height:12px;border-radius:12px;background-color:", ";transform:translateX(-50%);cursor:col-resize;"], color));
const Range = /*#__PURE__*/memo(({
  top,
  color,
  length,
  style,
  type,
  hide,
  expand,
  position,
  children,
  onMove,
  onRelease
}) => {
  const side = useRef();
  const scope = useScope();
  const ripple = useRipple();
  const [visible, setVisible] = useState(true);
  const onDrag = useDrag(useCallback(({
    args,
    last,
    first,
    event,
    movement
  }) => {
    event.stopPropagation();
    if (first) {
      side.current = args[0];
    } else if (last) {
      onRelease();
    } else {
      const size = getSize();
      const {
        width
      } = getRect();
      onMove(side.current, map(movement[0], 0, width, 0, size));
    }
  }, [onMove, onRelease]));
  useEffect(() => {
    if (ripple) setVisible(false);
  }, [ripple]);
  useDebounce(() => {
    if (!ripple) setVisible(true);
  }, 500, [ripple]);
  if (!visible || length <= 0) return false;
  const width = length / scope.length * 100;
  const left = (position - scope.start) / scope.length * 100;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Root, {
    top: top,
    hide: hide,
    type: type,
    color: color,
    style: {
      left: `${left}%`,
      width: `${width}%`,
      ...style
    }
  }, children && /*#__PURE__*/React.createElement(StyledLabel, {
    inverted: true,
    color: color
  }, children)), expand && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Handle, _extends({
    color: color,
    style: {
      left: `${left}%`,
      bottom: expand === "bottom" ? "5px" : "25px"
    }
  }, onDrag(0))), /*#__PURE__*/React.createElement(Handle, _extends({
    end: true,
    color: color,
    style: {
      left: `${left + width}%`,
      bottom: expand === "bottom" ? "5px" : "25px"
    }
  }, onDrag(1)))));
});
Range.defaultProps = {
  color: "default"
};
export default Range;
//# sourceMappingURL=Range.js.map